import { forceLogout, getOldRefresh, isInvalidTokenError, runRefresh } from '../refresh';
import { IRequestProps, IResponseProps } from '../types';

// refresh via fdm:// (mesmo canal do binario), nao via axios
function refreshAuthTokenFetch(): Promise<string> {
  return runRefresh(async () => {
    const oldRefresh = getOldRefresh();
    if (!oldRefresh) throw new Error('Refresh token não encontrado');

    const response = await fetch(`fdm:///auth`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({ oldRefresh })
    });

    if (!response.ok) throw new Error('Não foi possível renovar o token');

    return response.json();
  });
}

export async function fetchRequest<T>(config: IRequestProps, isRetry = false): Promise<IResponseProps<T>> {
  const response = await fetch(`fdm://${config.url}?` + new URLSearchParams(config.params), {
    method: config.method,
    ...(config.data ? { body: JSON.stringify(config.data) } : {}),
    headers: config.headers,
    priority: 'high',
    ...(config.signal ? { signal: config.signal } : {})
  });

  if (response.status === 401 && config.headers?.has('Authorization')) {
    const { handleSessionExpired } = await import('@/middleware/session-expired');
    handleSessionExpired();
  }

  if (!response.ok) {
    const responseData = await response.json();
    const message = responseData.message ?? response.statusText;

    // 401 de token invalido -> refresh + retry uma vez. /auth nao re-tenta p/ evitar loop.
    // So tenta refresh se a request saiu com Authorization: um 401 sem header
    // "request disparada antes da sessao estar pronta", nao "token expirado"
    // generica do backend ("Format is Authorization: Bearer [token]")
    // enganaria o isInvalidTokenError, levando a um forceLogout indevido.
    const isAuthRoute = config.url.includes('/auth');
    const hadAuthHeader = config.headers?.has('Authorization') ?? false;

    if (hadAuthHeader && isInvalidTokenError(response.status, message) && !isRetry && !isAuthRoute) {
      try {
        const token = await refreshAuthTokenFetch();
        config.headers?.set('Authorization', `Bearer ${token}`);
        return fetchRequest<T>(config, true);
      } catch {
        forceLogout();
      }
    }

    throw Object.assign(new Error(message), {
      statusCode: response.status,
      error: responseData.error ?? response.statusText
    });
  }

  const responseData = await response.json();

  return {
    data: responseData,
    headers: response.headers,
    status: response.status,
    statusText: response.statusText
  };
}
